import React, { useState } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { ntxAPI } from '@API/axiosNeatex';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import useGetAccounts from '@hooks/Finance/ChartAccountsDetails/useGetAccounts';
import Select from 'react-select';
import { Card } from '@components/card/card';

interface AccountStatement {
  id: string;
  jE_CATEGORY: string;
  jE_CATEGORY_NAME: string;
  jE_CATEGORY_NAME2: string;
  jE_SOURCE: string;
  jE_SOURCE_NAME: string;
  jE_SOURCE_NAME2: string;
  perioD_ID: string;
  perioD_NAME: string;
  perioD_NAME2: string;
  gL_DATE: string;
  amounT_DR: number | null;
  amounT_CR: number | null;
  jE_LINE_DESCRIPTION: string;
  codE_COMBINATION_CODE: string;
  account: string;
  accounT_NAME: string;
  accounT_NAME2: string;
  accounT_NUMBER: string;
  accounT_PARANT: string;
  accounT_TYPE: string;
  accounT_LEVEL: number;
}

const AccountStatementReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<AccountStatement[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { OptionsAccounts } = useGetAccounts();

  // حالات الفلتر
  const [filter, setFilter] = useState({
    accountNumber: '',
    startDate: '',
    endDate: ''
  });

  // دالة جلب البيانات مع الفلاتر
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await ntxAPI.get<{ succeeded: boolean; data: AccountStatement[]; message?: string }>(
        'Reports/GetAccountStatement',
        {
          params: {
            ACCOUNT_NUMBER: filter.accountNumber,
            StartDate: filter.startDate,
            EndDate: filter.endDate
          }
        }
      );
      if (data.succeeded === true) {
        setData(data.data || []);
      } else {
        setError(data.message || 'حدث خطأ غير معروف');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ غير معروف');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilter({
      accountNumber: '',
      startDate: '',
      endDate: ''
    });
  };

  const formatCurrency = (amount: number | null): string => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // const formatDate = (dateString: string): string => {
  //   return new Date(dateString).toLocaleDateString('en-US');
  // };

  // حساب الرصيد لكل إدخال
  const calculateBalance = (dr: number | null, cr: number | null) => {
    const debit = dr || 0;
    const credit = cr || 0;
    return debit - credit;
  };

  // حساب الإجماليات
  const calculateTotals = () => {
    const totalDebit = data.reduce((sum, item) => sum + (item.amounT_DR || 0), 0);
    const totalCredit = data.reduce((sum, item) => sum + (item.amounT_CR || 0), 0);
    const totalBalance = data.reduce((sum, item) => sum + calculateBalance(item.amounT_DR, item.amounT_CR), 0);

    return { totalDebit, totalCredit, totalBalance };
  };

  const { totalDebit, totalCredit, totalBalance } = calculateTotals();

  return (
    <>
      <div className='d-flex align-items-center'>
        <Breadcrumb
          BreadcrumbList={[
            {
              name: 'Home',
              link: ':moduleHome'
            },
            {
              name: 'كشف الحساب',
              link: null
            }
          ]}
          PageName='كشف الحساب'
        />
      </div>

      <Card className=' mb-4'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h6 className='mb-0'>
            <i className='fas fa-file-invoice me-2'></i>
            كشف الحساب
            <span className='badge bg-primary ms-2'>التقرير المالي</span>
          </h6>
          <div>
            <span className='badge bg-theme me-2'>إجمالي الحركات: {data?.length || 0}</span>
            <span className='badge bg-success'>الرصيد الإجمالي: {formatCurrency(totalBalance)}</span>
          </div>
        </div>

        <div className='card-body p-0'>
          {/* قسم الفلاتر */}
          <div className='p-3 border-bottom'>
            <div className='row mb-3'>
              <div className='col-lg-6 mb-2'>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text fw-bold'>رقم الحساب</span>

                  <Select
                    classNamePrefix='react-select'
                    className={`w-100`}
                    isLoading={loading}
                    isSearchable
                    isClearable
                    name='accountId'
                    options={OptionsAccounts}
                    placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                    onChange={(option: any) => {
                      // )
                      setFilter({ ...filter, accountNumber: option === null ? null : option.accountNumber });
                    }}
                    value={OptionsAccounts?.find(f => f.value === filter.accountNumber)}
                  />
                </div>
              </div>

              <div className='col-lg-3 mb-2'>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text fw-bold'>من تاريـخ</span>
                  <input
                    type='date'
                    autoComplete='off'
                    className='form-control'
                    value={filter.startDate}
                    onChange={e => {
                      setFilter({ ...filter, startDate: e.target.value });
                    }}
                  />
                </div>
              </div>

              <div className='col-lg-3 mb-2'>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text fw-bold'>إلى تاريـخ</span>
                  <input
                    type='date'
                    autoComplete='off'
                    className='form-control'
                    value={filter.endDate}
                    onChange={e => {
                      setFilter({ ...filter, endDate: e.target.value });
                    }}
                  />
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-lg-6'>
                <button className='btn btn-secondary me-2' onClick={resetFilters}>
                  إعادة تعيين
                </button>
                <button className='btn btn-theme' onClick={fetchData} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        as='span'
                        animation='border'
                        size='sm'
                        role='status'
                        aria-hidden='true'
                        className='me-2'
                      />
                      جاري التحميل...
                    </>
                  ) : (
                    'تحميل التقرير'
                  )}
                </button>
              </div>
            </div>

            {error && (
              <Alert variant='danger' className='mt-3 mb-0'>
                خطأ في تحميل البيانات: {error}
              </Alert>
            )}
          </div>

          {/* قسم النتائج */}
          <Loading loading={loading} error={error} Type='Dots'>
            <div className='table-responsive'>
              <table className='table table-hover mb-0'>
                <thead className='table-actived fw-bold'>
                  <tr>
                    <th className='text-center small'>التاريخ</th>
                    <th className='text-center small'>الحساب</th>
                    <th className='text-center small'>رقم الحساب</th>
                    <th className='text-center small'>التصنيف</th>
                    <th className='text-center small'>المصدر</th>
                    <th className='text-center small'>الوصف</th>
                    <th className='text-center small'>مدين</th>
                    <th className='text-center small'>دائن</th>
                    <th className='text-center small'>الرصيد</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 ? (
                    data.map((item, index) => {
                      const balance = calculateBalance(item.amounT_DR, item.amounT_CR);
                      return (
                        <tr key={`${item.id}-${index}`}>
                          <td className='text-center align-middle'>
                            <strong>{item.gL_DATE?.split('T')[0]}</strong>
                          </td>
                          <td className='text-center align-middle'>
                            <strong>
                              {i18n.language === 'en' ? item.accounT_NAME : item.accounT_NAME2 || item.accounT_NAME}
                            </strong>
                          </td>
                          <td className='text-center align-middle'>
                            <strong>{item.accounT_NUMBER}</strong>
                          </td>
                          <td className='text-center align-middle'>
                            <strong>
                              {i18n.language === 'en'
                                ? item.jE_CATEGORY_NAME
                                : item.jE_CATEGORY_NAME2 || item.jE_CATEGORY_NAME}
                            </strong>
                          </td>
                          <td className='text-center align-middle'>
                            <strong>
                              {i18n.language === 'en'
                                ? item.jE_SOURCE_NAME
                                : item.jE_SOURCE_NAME2 || item.jE_SOURCE_NAME}
                            </strong>
                          </td>
                          <td className='text-center align-middle'>{item.jE_LINE_DESCRIPTION || '-'}</td>
                          <td className='text-center align-middle'>
                            <span className='badge bg-danger fs-6'>{formatCurrency(item.amounT_DR)}</span>
                          </td>
                          <td className='text-center align-middle'>
                            <span className='badge bg-success fs-6'>{formatCurrency(item.amounT_CR)}</span>
                          </td>
                          <td className='text-center align-middle'>
                            <span
                              className={`badge fs-6 ${
                                balance > 0 ? 'bg-danger' : balance < 0 ? 'bg-success' : 'bg-secondary'
                              }`}
                            >
                              {formatCurrency(Math.abs(balance))}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={9} className='text-center py-4 text-muted'>
                        <i className='fa fa-inbox fa-2x mb-2'></i>
                        <br />
                        لا توجد سجلات للعرض
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Loading>
        </div>

        <div className='card-footer d-flex justify-content-between align-items-center'>
          <div className='text-muted small'>
            عرض {data?.length || 0} من {data?.length || 0} حركة
          </div>
          <div className='d-flex gap-3'>
            <span className='text-danger fw-bold'>إجمالي المدين: {formatCurrency(totalDebit)}</span>
            <span className='text-success fw-bold'>إجمالي الدائن: {formatCurrency(totalCredit)}</span>
            <span
              className={`fw-bold ${
                totalBalance > 0 ? 'text-danger' : totalBalance < 0 ? 'text-success' : 'text-muted'
              }`}
            >
              الرصيد النهائي: {formatCurrency(Math.abs(totalBalance))}
            </span>
          </div>
        </div>
      </Card>
    </>
  );
};

export default AccountStatementReport;
